/** Top-level routing. Public: landing + auth. Private: cabinet (under /app). */
import React, {lazy, Suspense} from "react";
import {Route, Routes} from "react-router-dom";

import {RequireAuth} from "/App";

const LandingPage = lazy(() => import("/pages/landing/LandingPage"));
const SignInPage = lazy(() => import("/pages/auth/SignInPage"));
const SignUpPage = lazy(() => import("/pages/auth/SignUpPage"));
const ConfirmEmailPage = lazy(() => import("/pages/auth/ConfirmEmailPage"));
const ConfirmEmailChangePage = lazy(() => import("/pages/auth/ConfirmEmailChangePage"));

const CabinetLayout = lazy(() => import("/layouts/CabinetLayout"));
const DashboardPage = lazy(() => import("/pages/cabinet/DashboardPage"));
const MyAppsPage = lazy(() => import("/pages/cabinet/MyAppsPage"));
const AppDetailPage = lazy(() => import("/pages/cabinet/AppDetailPage"));
const AnalyticsPage = lazy(() => import("/pages/cabinet/AnalyticsPage"));
const PayoutsPage = lazy(() => import("/pages/cabinet/PayoutsPage"));
const WebhooksPage = lazy(() => import("/pages/cabinet/WebhooksPage"));
const SandboxPage = lazy(() => import("/pages/cabinet/SandboxPage"));
const SettingsPage = lazy(() => import("/pages/cabinet/SettingsPage"));
const NotificationsPage = lazy(() => import("/pages/cabinet/NotificationsPage"));
const OnboardingPage = lazy(() => import("/pages/auth/OnboardingPage"));
const DocsPage = lazy(() => import("/pages/docs/DocsPage"));

const Fallback: React.FC = () => <div style={{padding: 40, textAlign: "center"}}>Loading…</div>;

const AppRoutes: React.FC = () => (
    <Suspense fallback={<Fallback />}>
        <Routes>
            {/* Public */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/sign-in" element={<SignInPage />} />
            <Route path="/sign-up" element={<SignUpPage />} />
            <Route path="/auth/confirm/:token" element={<ConfirmEmailPage />} />
            <Route path="/profile/confirm-email/:token" element={<ConfirmEmailChangePage />} />

            {/* Public docs viewer. /docs renders README.md by default;
             *  /docs/<file>.md (or /docs/<file>) fetches that file from
             *  /docs-raw/ (rsbuild copies it from project docs/). */}
            <Route path="/docs" element={<DocsPage />} />
            <Route path="/docs/*" element={<DocsPage />} />

            {/* Onboarding wizard — auth required, but not the full cabinet shell. */}
            <Route path="/onboarding/*" element={<RequireAuth><OnboardingPage /></RequireAuth>} />

            {/* Cabinet — auth + shared sidebar/topbar layout. */}
            <Route path="/app" element={<RequireAuth><CabinetLayout /></RequireAuth>}>
                <Route index element={<DashboardPage />} />
                <Route path="apps" element={<MyAppsPage />} />
                <Route path="apps/:appId/*" element={<AppDetailPage />} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="payouts" element={<PayoutsPage />} />
                <Route path="webhooks" element={<WebhooksPage />} />
                <Route path="sandbox" element={<SandboxPage />} />
                <Route path="settings" element={<SettingsPage />} />
                {/* Reached from the bell dropdown (#<uuid> focuses a row); not in the left nav. */}
                <Route path="notifications" element={<NotificationsPage />} />
            </Route>

            <Route path="*" element={<LandingPage />} />
        </Routes>
    </Suspense>
);

export default AppRoutes;
